import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Draggable from 'react-draggable';
import {MessageCircleDashed as MessageCircle, X, Send, User, Mail, Building, Phone, FileText, MessageSquare, Move, Bot, Sparkles, RefreshCw, ChevronRight, CheckSquare, Square, ClipboardList} from 'lucide-react';
import { lumi } from '../lib/lumi';
import toast from 'react-hot-toast';

interface ChatMessage {
  id: string;
  role: 'user' | 'ai';
  text: string;
  timestamp: Date;
  relatedLinks?: { title: string; url: string }[];
}

// 동의어 사전
const SYNONYMS: { [key: string]: string[] } = {
  '가격': ['비용', '요금', '얼마', '견적', '프라이싱', 'price', 'cost'],
  '문의': ['연락', '전화', '메일', '이메일', '상담', '번호', '위치', '주소', 'contact'],
  '기능': ['특징', '장점', '하는일', '역할', '스펙', 'feature'],
  '구축': ['설치', '도입', '시작', '기간', '일정'],
  '장점': ['좋은점', '이점', '혜택', 'benefit'],
  'CRM': ['고객관리', '씨알엠'],
  '녹취': ['레코딩', '녹음', '보이스', 'voice'],
};

const FloatingContactButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'email'>('chat');
  
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', phone: '', subject: '', message: ''
  });

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'ai',
      text: '안녕하세요! X:CONNECT 지능형 상담원입니다.\n\n"X:CRM 가격이 어떻게 되나요?"\n"녹취 솔루션 기능 알려줘"\n처럼 궁금한 점을 물어보세요.',
      timestamp: new Date()
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [knowledgeBase, setKnowledgeBase] = useState<{faqs: any[], solutions: any[]}>({ faqs: [], solutions: [] });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const nodeRef = useRef(null);

  useEffect(() => {
    if (isOpen && knowledgeBase.faqs.length === 0) loadKnowledgeBase();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // [보안 유지] 공개된 데이터만 AI에게 학습시킴
  const loadKnowledgeBase = async () => {
    try {
      const [faqs, solutions] = await Promise.all([
        lumi.entities.faqs.list({ filter: { published: true }, limit: 100 }),
        lumi.entities.solutions.list({ filter: { published: true, status: 'active' }, limit: 100 })
      ]);
      
      setKnowledgeBase({
        faqs: faqs.list || [],
        solutions: solutions.list || []
      });
    } catch (error) {
      console.error('지식 로딩 실패', error);
    }
  };

  // 텍스트 유사도 분석 (Scoring Logic)
  const calculateScore = (text: string, queryTokens: string[]): number => {
    let score = 0;
    const normalizedText = text.toLowerCase();
    
    queryTokens.forEach(token => {
      if (normalizedText.includes(token)) score += 10;
      Object.keys(SYNONYMS).forEach(key => {
        if (SYNONYMS[key].includes(token) && normalizedText.includes(key)) {
          score += 5;
        }
      });
    });
    return score;
  };

  const generateAIResponse = async (userQuery: string) => {
    setIsTyping(true);

    setTimeout(() => {
      const query = userQuery.toLowerCase();
      const tokens = query.split(/\s+/);
      
      let bestMatch = { type: '', item: null as any, score: 0 };

      // 1. 솔루션 검색
      knowledgeBase.solutions.forEach(s => {
        const titleScore = calculateScore(s.title, tokens) * 2;
        const descScore = calculateScore(s.description, tokens);
        const catScore = calculateScore(s.category, tokens);
        const featureScore = s.features ? calculateScore(s.features.join(' '), tokens) : 0;
        
        const total = titleScore + descScore + catScore + featureScore;
        if (total > bestMatch.score) bestMatch = { type: 'solution', item: s, score: total };
      });

      // 2. FAQ 검색
      knowledgeBase.faqs.forEach(f => {
        const qScore = calculateScore(f.question, tokens) * 2;
        const aScore = calculateScore(f.answer, tokens);
        const total = qScore + aScore;
        
        if (total > bestMatch.score) bestMatch = { type: 'faq', item: f, score: total };
      });

      let responseText = '';
      let links: { title: string; url: string }[] = [];

      if (bestMatch.score > 5) {
        if (bestMatch.type === 'solution') {
          const s = bestMatch.item;
          responseText = `[${s.title}] 솔루션이 고객님께 적합해 보입니다!\n\n💡 특징: ${s.description}\n\n주요 기능으로는 ${(s.features || []).slice(0, 3).join(', ')} 등이 있습니다.`;
          if (s.pricingPlan && (query.includes('가격') || query.includes('비용') || query.includes('요금'))) {
            responseText += `\n\n💰 요금제: ${(s.pricingPlan || []).join(', ')}`;
          }
          links.push({ title: `${s.title} 자세히 보기`, url: `/solutions/${s._id}` });
        } else {
          const f = bestMatch.item;
          responseText = `관련된 정보를 찾았습니다.\n\nQ: ${f.question}\n\nA: ${f.answer}`;
        }
      } else {
        if (query.includes('안녕') || query.includes('반가')) {
          responseText = "안녕하세요! X:CONNECT입니다. 솔루션 도입이나 기술 지원에 대해 무엇이든 물어보세요.";
        } else if (query.includes('위치') || query.includes('주소')) {
          responseText = "본사는 '인천시 부평구 대정로 7'에 위치해 있습니다. 상세 약도는 문의 페이지에서 확인 가능합니다.";
          links.push({ title: '오시는 길 확인', url: '/contact' });
        } else if (query.includes('연락') || query.includes('전화')) {
          responseText = "대표 전화는 1644-2262 입니다. (평일 09:00 - 18:00)";
        } else {
          responseText = "죄송합니다. 질문의 의도를 정확히 파악하지 못했습니다.\n\n다음과 같이 질문해 보시는 건 어떨까요?\n- 'CRM 가격이 얼마야?'\n- '녹취 시스템 기능 알려줘'\n\n상세한 답변이 필요하시다면 상단의 '상담 문의' 탭을 이용해 주세요.";
        }
      }

      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'ai',
        text: responseText,
        timestamp: new Date(),
        relatedLinks: links
      }]);
      setIsTyping(false);
    }, 800);
  };

  const handleSendMessage = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: chatInput,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const query = chatInput;
    setChatInput('');
    
    generateAIResponse(query);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      toast.error('개인정보 수집 및 이용에 동의해주세요');
      return;
    }
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('필수 항목을 모두 입력해주세요');
      return;
    }

    setLoading(true);
    try {
      await lumi.entities.contacts.create({
        ...formData,
        status: 'new',
        createdAt: new Date().toISOString()
      });
      toast.success('문의가 성공적으로 접수되었습니다!');
      setFormData({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
      setAgreed(false);
      setIsOpen(false);
    } catch (error) {
      toast.error('전송 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-8 right-8 z-50 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-full shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 group"
      >
        <div className="relative">
          <MessageCircle size={28} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse border-2 border-white"></span>
        </div>
        {/* 툴팁 텍스트 변경 */}
        <div className="absolute right-full mr-4 top-1/2 transform -translate-y-1/2 bg-white text-gray-800 px-4 py-2 rounded-lg shadow-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden md:block">
          무엇이든 물어보세요!
        </div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            />

            <Draggable handle=".drag-handle" nodeRef={nodeRef}>
              <div ref={nodeRef} className="fixed inset-0 m-auto h-fit w-full max-w-md md:max-w-2xl max-h-[85vh] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-100">
                
                <div className="flex-shrink-0 bg-gradient-to-r from-blue-600 to-indigo-700 p-6 pb-0 drag-handle cursor-move">
                  <div className="flex justify-between items-start mb-6">
                    <div className="text-white">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Bot size={28} /> X:CONNECT 고객센터
                      </h2>
                      <p className="text-blue-100 text-sm mt-1 opacity-90">AI 챗봇과 전문 상담원이 도와드립니다.</p>
                    </div>
                    <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors">
                      <X size={24} />
                    </button>
                  </div>

                  <div className="flex space-x-1">
                    <button onClick={() => setActiveTab('chat')} className={`flex-1 py-3 text-sm font-medium rounded-t-lg flex items-center justify-center gap-2 transition-colors ${activeTab === 'chat' ? 'bg-white text-blue-700' : 'bg-white/10 text-blue-100 hover:bg-white/20'}`}>
                      <Sparkles size={16} /> AI 챗봇 상담
                    </button>
                    
                    {/* [변경됨] 이메일 문의 -> 상담 문의 */}
                    <button onClick={() => setActiveTab('email')} className={`flex-1 py-3 text-sm font-medium rounded-t-lg flex items-center justify-center gap-2 transition-colors ${activeTab === 'email' ? 'bg-white text-blue-700' : 'bg-white/10 text-blue-100 hover:bg-white/20'}`}>
                      <ClipboardList size={16} /> 상담 문의
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden bg-gray-50 flex flex-col">
                  
                  {activeTab === 'chat' && (
                    <div className="flex flex-col h-[500px]">
                      <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.map((msg) => (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            key={msg.id}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[80%] rounded-2xl p-4 ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-none' : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-none'}`}>
                              {msg.role === 'ai' && <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100"><Bot size={16} className="text-blue-600" /><span className="text-xs font-bold text-blue-600">AI Assistant</span></div>}
                              <div className="whitespace-pre-wrap text-sm leading-relaxed">{msg.text}</div>
                              {msg.relatedLinks && msg.relatedLinks.length > 0 && (
                                <div className="mt-3 pt-2 border-t border-gray-100 space-y-1">
                                  {msg.relatedLinks.map((link, idx) => (
                                    <a key={idx} href={link.url} className="flex items-center text-xs text-blue-500 hover:text-blue-700 hover:underline bg-blue-50 p-2 rounded transition-colors"><ChevronRight size={12} className="mr-1"/> {link.title}</a>
                                  ))}
                                </div>
                              )}
                              <div className={`text-[10px] mt-1 text-right ${msg.role === 'user' ? 'text-blue-200' : 'text-gray-400'}`}>{msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                            </div>
                          </motion.div>
                        ))}
                        {isTyping && <div className="flex justify-start"><div className="bg-white p-4 rounded-2xl rounded-bl-none shadow-sm border border-gray-100"><div className="flex space-x-1"><div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div><div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div><div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div></div></div></div>}
                        <div ref={messagesEndRef} />
                      </div>
                      <div className="p-4 bg-white border-t border-gray-100">
                        <form onSubmit={handleSendMessage} className="flex gap-2">
                          <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="질문을 입력하세요 (예: 가격, 기능)..." className="flex-1 border border-gray-300 rounded-full px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm" />
                          <button type="submit" disabled={!chatInput.trim() || isTyping} className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-md"><Send size={20} /></button>
                        </form>
                      </div>
                    </div>
                  )}

                  {activeTab === 'email' && (
                    <div className="flex-1 overflow-y-auto p-6 md:p-8 max-h-[500px]">
                      <form onSubmit={handleEmailSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">이름 *</label><input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="w-full border rounded-lg p-2" placeholder="홍길동" /></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">이메일 *</label><input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full border rounded-lg p-2" placeholder="user@example.com" /></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">회사명</label><input type="text" name="company" value={formData.company} onChange={handleInputChange} className="w-full border rounded-lg p-2" /></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">연락처</label><input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full border rounded-lg p-2" /></div>
                        </div>
                        <div><label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label><input type="text" name="subject" value={formData.subject} onChange={handleInputChange} required className="w-full border rounded-lg p-2" /></div>
                        <div><label className="block text-sm font-medium text-gray-700 mb-1">문의 내용 *</label><textarea name="message" value={formData.message} onChange={handleInputChange} required rows={4} className="w-full border rounded-lg p-2 resize-none" /></div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                           <button type="button" onClick={() => setAgreed(!agreed)} className={`${agreed ? 'text-blue-600' : 'text-gray-300'}`}>{agreed ? <CheckSquare size={18}/> : <Square size={18}/>}</button>
                           <label onClick={() => setAgreed(!agreed)} className="cursor-pointer">[필수] 개인정보 수집 및 이용에 동의합니다.</label>
                        </div>

                        <div className="flex justify-end pt-2">
                          {/* [변경됨] 버튼 텍스트: 문의하기 -> 상담 신청하기 */}
                          <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
                            {loading ? '전송 중...' : '상담 신청하기'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                </div>
              </div>
            </Draggable>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingContactButton;